import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import {
  GitCompare, Loader2, Plus, X, Stethoscope, Search,
} from "lucide-react";
import DashboardLayout, { useTheme } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPARE_COLORS = ["#10b981", "#06b6d4", "#f59e0b", "#ec4899", "#8b5cf6", "#3b82f6"];

const RADAR_METRICS = [
  { key: "approval_rate", label: "Approval %", max: 100 },
  { key: "first_pass_rate", label: "First-Pass %", max: 100 },
  { key: "completeness_score", label: "Completeness", max: 100 },
  { key: "patient_satisfaction", label: "Pt. Sat", max: 100 },
  { key: "turnaround_score", label: "Speed", max: 100 }, // derived
];

export default function ProviderCompare() {
  return (
    <DashboardLayout>
      <ProviderCompareInner />
    </DashboardLayout>
  );
}

function ProviderCompareInner() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProviders, setAllProviders] = useState([]);
  const [selectedNpis, setSelectedNpis] = useState(() => {
    const param = searchParams.get("npis");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [search, setSearch] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load provider directory
  useEffect(() => {
    axios.get(`${API}/analytics/providers`, { withCredentials: true })
      .then(r => setAllProviders(r.data.providers || []))
      .catch(() => {});
  }, []);

  // Fetch compare data when selection changes
  useEffect(() => {
    if (selectedNpis.length === 0) {
      setCompareData(null);
      setSearchParams({});
      return;
    }
    setSearchParams({ npis: selectedNpis.join(",") });
    setLoading(true);
    axios.get(`${API}/insights/provider-compare`, {
      params: { npis: selectedNpis.join(",") },
      withCredentials: true,
    })
      .then(r => setCompareData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Compare failed"))
      .finally(() => setLoading(false));
  }, [selectedNpis, setSearchParams]);

  const togglePick = (npi) => {
    setSelectedNpis(prev => {
      if (prev.includes(npi)) return prev.filter(x => x !== npi);
      if (prev.length >= 6) { toast.warning("Max 6 providers"); return prev; }
      return [...prev, npi];
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allProviders.filter(p =>
      !q || (p.npi || "").toLowerCase().includes(q)
        || (p.name || "").toLowerCase().includes(q)
        || (p.specialty || "").toLowerCase().includes(q)
    );
  }, [allProviders, search]);

  const surfaceCard = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-gray-200";
  const textTitle = isDark ? "text-white" : "text-gray-900";
  const axisColor = isDark ? "#64748b" : "#475569";

  const radarData = useMemo(() => {
    if (!compareData?.providers) return [];
    return RADAR_METRICS.map(m => {
      const point = { metric: m.label };
      compareData.providers.forEach((p, i) => {
        let val = p[m.key];
        // turnaround_score: invert so faster = higher
        if (m.key === "turnaround_score") {
          val = Math.max(0, 100 - p.avg_turnaround_hrs * 2);
        }
        point[`p${i}`] = Number((val || 0).toFixed(1));
      });
      return point;
    });
  }, [compareData]);

  return (
    <div data-testid="provider-compare-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GitCompare className="w-5 h-5 text-emerald-500" />
            <Badge variant="outline" className="text-xs">PriorAuth Pro</Badge>
          </div>
          <h1 className={`text-3xl font-bold ${textTitle}`}>Provider Comparison</h1>
          <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            Compare up to 6 NPIs side-by-side across approval, turnaround, payer mix and trends.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/provider-performance")}
          data-testid="back-to-perf-btn">
          <Stethoscope className="w-4 h-4 mr-2" /> Single Provider View
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Picker */}
        <div className="col-span-12 lg:col-span-4">
          <Card className={`${surfaceCard} sticky top-20`}>
            <CardHeader>
              <CardTitle className={`text-base ${textTitle}`}>
                Select Providers ({selectedNpis.length}/6)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="compare-search-input"
                  placeholder="Search NPI, name, specialty..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={`pl-9 ${isDark ? "bg-slate-800 border-slate-700 text-white" : ""}`}
                />
              </div>

              {/* Selected chips */}
              {selectedNpis.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {selectedNpis.map((npi, i) => (
                    <Badge key={npi} variant="outline" className="text-xs flex items-center gap-1"
                      style={{ borderColor: COMPARE_COLORS[i], color: COMPARE_COLORS[i] }}>
                      {npi}
                      <button onClick={() => togglePick(npi)} data-testid={`chip-remove-${npi}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="max-h-[480px] overflow-y-auto space-y-1.5">
                {filtered.length === 0 && (
                  <div className={`text-sm text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                    No providers found. Use Insights demo data or create a PA first.
                  </div>
                )}
                {filtered.map(p => {
                  const picked = selectedNpis.includes(p.npi);
                  return (
                    <button key={p.npi}
                      data-testid={`pick-${p.npi}`}
                      onClick={() => togglePick(p.npi)}
                      className={`w-full text-left p-2.5 rounded-md text-sm transition-colors ${
                        picked
                          ? (isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-blue-50 border border-blue-200")
                          : (isDark ? "border border-slate-800 hover:bg-slate-800" : "border border-gray-200 hover:bg-gray-50")
                      }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`font-medium ${textTitle}`}>{p.name || `NPI ${p.npi}`}</div>
                          <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"} font-mono`}>{p.npi}</div>
                          <div className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>{p.specialty || "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>PAs</div>
                          <div className={`text-sm font-mono ${textTitle}`}>{p.total_pas}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Quick add by NPI number */}
              <div className={`mt-4 pt-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <div className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>Or add demo NPI:</div>
                <div className="flex gap-2 flex-wrap">
                  {["1234567890", "9876543210", "5556667770", "1112223334"].map(demo => (
                    <Button key={demo} size="sm" variant="outline" onClick={() => togglePick(demo)}
                      disabled={selectedNpis.includes(demo)}
                      data-testid={`add-demo-${demo}`}>
                      <Plus className="w-3 h-3 mr-1" /> {demo.slice(-4)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparison view */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {selectedNpis.length === 0 && (
            <Card className={surfaceCard}>
              <CardContent className="p-16 text-center">
                <GitCompare className="w-12 h-12 mx-auto opacity-30 mb-3" />
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  Select at least 1 provider to begin comparison
                </p>
              </CardContent>
            </Card>
          )}

          {loading && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>}

          {compareData && !loading && (
            <>
              {/* Profile strip */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {compareData.providers.map((p, i) => (
                  <Card key={p.npi} className={surfaceCard}
                    style={{ borderLeftWidth: 4, borderLeftColor: COMPARE_COLORS[i] }}
                    data-testid={`compare-profile-${p.npi}`}>
                    <CardContent className="p-4">
                      <div className={`text-sm font-semibold ${textTitle}`}>{p.name}</div>
                      <div className={`text-xs font-mono ${isDark ? "text-slate-400" : "text-gray-500"}`}>{p.npi}</div>
                      <div className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>{p.specialty}</div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Total PAs</div>
                          <div className={`text-base font-mono font-semibold ${textTitle}`}>{p.total_pas}</div>
                        </div>
                        <div>
                          <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Approval</div>
                          <div className="text-base font-mono font-semibold text-emerald-500">{p.approval_rate.toFixed(1)}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* KPI comparison bar */}
              <Card className={surfaceCard}>
                <CardHeader><CardTitle className={`text-base ${textTitle}`}>Key Metrics Comparison</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={compareData.providers.map((p, i) => ({
                      name: p.name.length > 18 ? p.name.slice(0, 18) + "…" : p.name,
                      "Approval %": p.approval_rate,
                      "First-Pass %": p.first_pass_rate,
                      "Completeness": p.completeness_score,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                      <XAxis dataKey="name" tick={{ fill: axisColor, fontSize: 10 }} />
                      <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                      <Bar dataKey="Approval %" fill={COMPARE_COLORS[0]} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="First-Pass %" fill={COMPARE_COLORS[1]} radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Completeness" fill={COMPARE_COLORS[2]} radius={[6, 6, 0, 0]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Radar */}
              <Card className={surfaceCard}>
                <CardHeader><CardTitle className={`text-base ${textTitle}`}>Multi-Dimensional Performance</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={360}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: axisColor, fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fill: axisColor, fontSize: 10 }} angle={90} domain={[0, 100]} />
                      {compareData.providers.map((p, i) => (
                        <Radar key={p.npi} name={p.name}
                          dataKey={`p${i}`}
                          stroke={COMPARE_COLORS[i]}
                          fill={COMPARE_COLORS[i]}
                          fillOpacity={0.15}
                          strokeWidth={2} />
                      ))}
                      <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Trend overlay */}
              <Card className={surfaceCard}>
                <CardHeader><CardTitle className={`text-base ${textTitle}`}>Approval Rate Trend (6 mo)</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
                      <XAxis dataKey="month" type="category" allowDuplicatedCategory={false}
                        tick={{ fill: axisColor, fontSize: 10 }}
                        data={compareData.providers[0]?.trend || []} />
                      <YAxis tick={{ fill: axisColor, fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip contentStyle={{ background: isDark ? "#0f172a" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }} />
                      {compareData.providers.map((p, i) => (
                        <Line key={p.npi}
                          data={p.trend}
                          name={p.name}
                          dataKey="approval_rate"
                          stroke={COMPARE_COLORS[i]}
                          strokeWidth={2}
                          dot={{ r: 3 }} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Benchmark */}
              {compareData.benchmark && (
                <Card className={surfaceCard}>
                  <CardHeader><CardTitle className={`text-base ${textTitle}`}>Peer Benchmark</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <Metric label="Approval %" value={`${compareData.benchmark.approval_rate}%`} isDark={isDark} />
                      <Metric label="Turnaround" value={`${compareData.benchmark.avg_turnaround_hrs}h`} isDark={isDark} />
                      <Metric label="First-Pass" value={`${compareData.benchmark.first_pass_rate}%`} isDark={isDark} />
                      <Metric label="Completeness" value={`${compareData.benchmark.completeness_score}%`} isDark={isDark} />
                      <Metric label="Rework" value={`${compareData.benchmark.rework_pct}%`} isDark={isDark} />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Side-by-side payer mix */}
              <Card className={surfaceCard}>
                <CardHeader><CardTitle className={`text-base ${textTitle}`}>By Payer — Approval %</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-gray-200 text-gray-600"}`}>
                          <th className="text-left py-2 px-3">Provider</th>
                          {Array.from(new Set(compareData.providers.flatMap(p => p.by_payer.map(x => x.payer)))).slice(0, 6).map(payer => (
                            <th key={payer} className="text-right py-2 px-3">{payer}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {compareData.providers.map((p, i) => {
                          const payers = Array.from(new Set(compareData.providers.flatMap(x => x.by_payer.map(y => y.payer)))).slice(0, 6);
                          return (
                            <tr key={p.npi} className={`border-b ${isDark ? "border-slate-800/40 text-slate-300" : "border-gray-100 text-gray-700"}`}>
                              <td className="py-2 px-3">
                                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COMPARE_COLORS[i] }} />
                                {p.name}
                              </td>
                              {payers.map(payer => {
                                const found = p.by_payer.find(b => b.payer === payer);
                                return <td key={payer} className="text-right py-2 px-3 font-mono">
                                  {found ? `${found.approval_rate.toFixed(1)}% (${found.total})` : "—"}
                                </td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, isDark }) {
  return (
    <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
      <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{label}</div>
      <div className={`text-base font-mono font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
